import React from 'react';
import { HTTP, ScreenName } from '@helpcenter/constants';
import { ElementErrorUI } from '@helpcenter/layout-primitives-util/src/element-error-ui';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import { ElementErrorBoundary } from 'view/layout-primitives/element-error-boundary';
import messages from 'view/layout-primitives/element-error-boundary/messages';
import { HelpCenterAnnouncement } from '@atlassian/help-center-announcement-element';
import { isCSMHelpCenter } from '@atlassian/help-center-common-util/advanced-help-center';
import { FetchError } from '@atlassian/help-center-common-util/fetch/fetch';
import EditorViewerComponent from '@atlassian/help-center-editor-element/src/ui/editor-view-renderer';
import { HeroElement } from '@atlassian/help-center-hero-element';
import { ConnectElement } from '@atlassian/help-layout-connect-element';
import { ForgeElement } from '@atlassian/help-layout-forge-element';
import { ListOfPortals } from '@atlassian/list-of-portals-element';
import { ListOfTopics } from '@atlassian/list-of-topics-element';
import { NoContent } from '@atlassian/no-content-element';
import { SuggestedRequestForms } from '@atlassian/suggested-request-forms-element';
import type { homePageElementViewRendererFragment$key } from './__generated__/homePageElementViewRendererFragment.graphql';

export interface HomePageElementViewRendererProps {
	element: homePageElementViewRendererFragment$key;
	parentSubsectionId: string;
}

export const HomePageElementViewRenderer = ({
	element: elementKey,
}: HomePageElementViewRendererProps) => {
	di(
		EditorViewerComponent,
		HeroElement,
		HelpCenterAnnouncement,
		ListOfTopics,
		ListOfPortals,
		SuggestedRequestForms,
		isCSMHelpCenter,
	);
	const { formatMessage } = useIntl();
	const elementData = useFragment(
		graphql`
			fragment homePageElementViewRendererFragment on HelpLayoutElement {
				__typename
				... on HelpLayoutEditorElement {
					# It does not recognize this valid fragment spread since we are using a re-export from the rich-text package's entry point.
					# eslint-disable-next-line relay/must-colocate-fragment-spreads
					...editorViewRendererFragment @dangerously_unaliased_fixme
				}
				... on HelpLayoutPortalsListElement {
					# It does not recognize this valid fragment spread since we are using a re-export from the request forms package's entry point.
					# It does not report an error if we consume this from the module - list-of-portals, directly.
					# eslint-disable-next-line relay/must-colocate-fragment-spreads
					...listOfPortalsOnHelpCenter_HelpLayoutListOfHelpCenterPortalsAtomicElement
						@dangerously_unaliased_fixme
				}
				... on HelpLayoutSuggestedRequestFormsListElement {
					# It does not recognize this valid fragment spread since we are using a re-export from the request forms package's entry point.
					# It does not report an error if we consume this from the module - suggested-request-forms, directly.
					# eslint-disable-next-line relay/must-colocate-fragment-spreads
					...suggestedRequestForms_HelpLayoutListOfHelpCenterSuggestedRequestFormsAtomicElement
						@dangerously_unaliased_fixme
				}
				... on HelpLayoutAnnouncementElement {
					# It does not recognize this valid fragment spread since we are using a re-export from the request forms package's entry point.
					# It does not report an error if we consume this from the module - help-center-announcement, directly.
					# eslint-disable-next-line relay/must-colocate-fragment-spreads
					...helpCenterAnnouncementFragment @dangerously_unaliased_fixme
				}
				... on HelpLayoutTopicsListElement {
					...listOfTopicsElementFragment @dangerously_unaliased_fixme
				}
				... on HelpLayoutHeroElement {
					# It does not recognize this valid fragment spread since we are using a re-export from the request forms package's entry point.
					# It does not report an error if we consume this from the module - help-center-hero-element, directly.
					# eslint-disable-next-line relay/must-colocate-fragment-spreads
					...heroElementFragment @dangerously_unaliased_fixme
				}
				... on HelpLayoutNoContentElement {
					__typename
				}
				... on HelpLayoutForgeElement {
					__typename
					# eslint-disable-next-line relay/must-colocate-fragment-spreads
					...forgeElementFragment @dangerously_unaliased_fixme
				}
				... on HelpLayoutConnectElement {
					__typename
					# eslint-disable-next-line relay/must-colocate-fragment-spreads
					...connectElementFragment @dangerously_unaliased_fixme
				}
				... on QueryError {
					identifier
					extensions {
						statusCode
					}
				}
			}
		`,
		elementKey,
	);

	switch (elementData.__typename) {
		case 'HelpLayoutEditorElement': {
			return <EditorViewerComponent editorElement={elementData} />;
		}
		case 'HelpLayoutHeroElement': {
			return <HeroElement heroElement={elementData} />;
		}
		case 'HelpLayoutAnnouncementElement': {
			return <HelpCenterAnnouncement announcementElement={elementData} />;
		}
		case 'HelpLayoutTopicsListElement': {
			return <ListOfTopics topicsElement={elementData} />;
		}
		case 'HelpLayoutPortalsListElement': {
			return <ListOfPortals listOfHelpCenters={elementData} />;
		}
		case 'HelpLayoutSuggestedRequestFormsListElement': {
			return <SuggestedRequestForms forms={elementData} />;
		}
		case 'HelpLayoutForgeElement': {
			return <ForgeElement forgeElementRef={elementData} />;
		}
		case 'HelpLayoutConnectElement': {
			return <ConnectElement connectElementRef={elementData} />;
		}
		case 'HelpLayoutNoContentElement': {
			return <NoContent />;
		}
		case 'QueryError': {
			const elementIdentifier =
				(elementData as unknown as { identifier: string }).identifier || 'UNKNOWN';
			// Warning: the below non-isomorphic way of coding is not recommended and please do not adopt this practice elsewhere.
			if (__SERVER__) {
				return (
					<ElementErrorUI
						errorMessage={formatMessage(messages.errorMessage)}
						elementType={
							elementIdentifier === 'UNKNOWN' ? elementData.__typename : elementIdentifier
						}
					/>
				);
			}
			let statusCode = HTTP.SERVER;

			if (
				elementData.extensions &&
				Array.isArray(elementData.extensions) &&
				elementData.extensions.length > 0
			) {
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
				statusCode = elementData.extensions[0].statusCode;
			}
			throw new FetchError(`${elementIdentifier} content failed to load`, statusCode, true);
		}
		default: {
			return null;
		}
	}
};

// TODO: Break this into separate files and add VR tests
const HomePageElementViewRendererWithErrorBoundary = (props: HomePageElementViewRendererProps) => {
	return (
		/**
		 * ErrorBoundary returns null unless renderError prop is passed,
		 * if we want custom error message per element, individual elements can be wrapped with this error boundary
		 */
		<ElementErrorBoundary screenName={ScreenName.VIEW_HOMEPAGE} helpLayoutElement={props.element}>
			<HomePageElementViewRenderer {...props} />
		</ElementErrorBoundary>
	);
};

export default HomePageElementViewRendererWithErrorBoundary;
